import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, Paper, Skeleton, Typography, useTheme } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Button, CardPreOferta, PageHeader, StepActions } from "@components/ui_v2";
import { SliderCotizador } from "@components/ui_v2/cotizadoresComponents";
import { getSegmentColor } from "@components/factoraje";
import { formatCurrencyWhole } from "@helpers/funciones";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setNewConditionLoan } from "@store/slices/registerSlice";
import {
  useLazyGetConditionsByIdQuery,
  useLazyGetLoanApplicationByIdQuery,
  useLazyGetTaxSystemConfigQuery,
  usePostConditionMutation,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { ApiError, FactorajeCompanyItem, LoanCondiciones, TaxSystemConfigResponse } from "@interfaces/response";
import { ALTO_SKELETON_EXPEDIENTE, STATUS_CATALOG } from "@helpers/constantes";
import { EJEMPLO_DIAS } from "@helpers/factoraje.helpers";
import useStatusConfig from "@hooks/useStatusConfig";

/**
 * TODO: datos dummy (los de `FX_PreAprobacion` del diseño) de lo que los servicios todavía no traen: el aforo, la
 * factura del ejemplo y los límites por empresa.
 */
const LINEA_DUMMY = {
  aforo: 90,
  facturaEjemplo: 500_000,
};

const LIMITES_POR_EMPRESA_DUMMY: FactorajeCompanyItem[] = [
  { name: "Nueva Wal-Mart de México", maxAmount: 3_900_000, maxTerm: 120 },
  { name: "Grupo Bimbo", maxAmount: 2_600_000, maxTerm: 90 },
  { name: "Cadena Comercial OXXO", maxAmount: 1_950_000, maxTerm: 60 },
  { name: "El Puerto de Liverpool", maxAmount: 1_625_000, maxTerm: 90 },
];

/** `.ld-offer-stat-k` / `.ld-offer-stat-v` del diseño, para las cifras de la card de ejemplo. */
const STAT_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "tokens.textTertiary",
  mb: "3px",
} as const;
const STAT_VALUE_SX = { fontSize: 19, fontWeight: 600, color: "primary.dark", fontVariantNumeric: "tabular-nums" };

/** `.card-title` / `.card-sub` del diseño. */
const CardTitulo = ({ title, sub }: { title: string; sub: string }) => (
  <>
    <Typography sx={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark", mb: "4px" }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: "tokens.textTertiary", lineHeight: 1.55 }}>{sub}</Typography>
  </>
);

/**
 * Línea de factoraje preaprobada de Flux (`FX_PreAprobacion` del diseño): la línea revolvente con sus condiciones
 * (el usuario puede tomar una menor), el límite por empresa y un ejemplo de operación.
 *
 * - Condición activa (mismo servicio que `OfertaDefault`): la línea elegida (`selected_amount`), el tope
 *   (`approved_amount`), el plazo (`approved_term`) y la tasa (`interest_rate`).
 * - Solicitud: la comisión por operación (`opening_fee`) y el régimen (`tax_systems.code`).
 * - Config del régimen (`getTaxSystemConfig`): el monto mínimo y el paso del slider para elegir una línea menor.
 *
 * Si el usuario toma una línea distinta, al aceptar se crea una condición nueva con ese monto. Lo demás sigue con
 * datos dummy (`LINEA_DUMMY`).
 */
export const OfertaFactoraje = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(STATUS_CATALOG.oferta.code);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [postCondition] = usePostConditionMutation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [taxSystemConfig, setTaxSystemConfig] = useState<TaxSystemConfigResponse | null>(null);
  const [comision, setComision] = useState(0);
  // Línea que va a tomar: la seleccionada o una menor que la aprobada, nunca mayor.
  const [linea, setLinea] = useState(0);
  const [editando, setEditando] = useState(false);

  useEffect(() => {
    const fetchOferta = async () => {
      try {
        const [conditions, loanApplication] = await Promise.all([
          getConditions(application.id).unwrap(),
          getLoanApplication(application.id).unwrap(),
        ]);
        const active = conditions.find((item) => item.active) ?? null;
        setCondition(active);
        if (active) {
          const seleccionada = active.selected_amount || active.loan_amount;
          setLinea(Math.min(seleccionada, active.approved_amount || seleccionada));
        }
        setComision(loanApplication.opening_fee ?? 0);
        const taxSystemCode = loanApplication.tax_systems?.code;
        if (taxSystemCode) {
          setTaxSystemConfig(await getTaxSystemConfig(taxSystemCode).unwrap());
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "OfertaFactoraje-initial_fetch" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };
    fetchOferta();
  }, [application.id, getConditions, getLoanApplication, getTaxSystemConfig, dispatch]);

  const lineaPreaprobada = condition?.approved_amount ?? 0;
  const tasa = condition?.interest_rate ?? 0;
  const plazo = condition?.approved_term ?? 0;
  // Sin config del régimen, el slider va de la línea aprobada a sí misma (no se puede elegir una menor).
  const lineaMin = Math.min(taxSystemConfig?.minAmount || lineaPreaprobada, lineaPreaprobada);
  const lineaStep = taxSystemConfig?.stepAmount || 1;
  const { aforo, facturaEjemplo } = LINEA_DUMMY;
  const anticipo = (facturaEjemplo * aforo) / 100;
  const costoEjemplo = anticipo * (tasa / 100) * (EJEMPLO_DIAS / 360) + facturaEjemplo * (comision / 100);

  // "Aceptar y asignar mi línea": si cambió la línea, condición nueva con ese monto; luego avanza al siguiente
  // status de `status_config` y navega a su pantalla.
  const handleAccept = async () => {
    try {
      setIsSubmitting(true);
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }
      if (condition && linea !== (condition.selected_amount || condition.loan_amount)) {
        await postCondition({
          loan_application: application.id,
          loan_amount: linea,
          requested_amount: linea,
          selected_amount: linea,
          requested_term: condition.selected_term,
          selected_term: condition.selected_term,
          user_name: `TOFU - ${accountUser.email}`,
          user_id: String(accountUser.id),
        }).unwrap();
        dispatch(
          setNewConditionLoan({
            loanAmount: linea,
            loanTerm: condition.selected_term,
            amountMonthly: condition.monthly_payment,
            approved_amount: condition.approved_amount,
            approved_term: condition.approved_term,
            interest_rate: condition.interest_rate,
          }),
        );
      }
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "OfertaFactoraje-accept" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Análisis · Línea preaprobada"
        title="Tu línea de factoraje está preaprobada"
        subtitle={
          isLoading ? undefined : (
            <>
              Tienes una línea revolvente preautorizada de <strong>{formatCurrencyWhole(lineaPreaprobada)} MXN</strong>.
              Cada factura que descuentes libera saldo conforme te pagan tus clientes.
            </>
          )
        }
      />

      {isLoading ? (
        <Skeleton variant="rectangular" height={ALTO_SKELETON_EXPEDIENTE} sx={{ borderRadius: 2 }} />
      ) : (
        <>
          <CardPreOferta
            eyebrow="Línea revolvente · Factoraje electrónico"
            badge="Preaprobada"
            amount={formatCurrencyWhole(linea)}
            amountCaption="MXN disponibles"
            amountAction={
              <Button
                tone="compact"
                startIcon={editando ? undefined : <EditOutlined sx={{ fontSize: 12 }} />}
                onClick={() => setEditando((value) => !value)}
                data-testid="oferta-factoraje-editar-linea-button"
              >
                {editando ? "Listo" : "Seleccionar menor monto de línea"}
              </Button>
            }
            sub="Vigente por 30 días · Sujeta a revisión documental y validación de cada factura"
            items={[
              { label: "Aforo", value: `${aforo}%` },
              { label: "Tasa anual", value: `${tasa}%` },
              { label: "Comisión por operación", value: `${comision}%` },
              { label: "Plazo máximo de factura", value: `${plazo} días` },
            ]}
          >
            {editando && (
              // `.fxu-edit-row`: separado de la cifra por la línea de la marca (Flux: #E5E5DA).
              <Box sx={{ mt: 2, pt: "14px", borderTop: "1px solid", borderColor: "divider" }}>
                <SliderCotizador
                  value={linea}
                  onChange={(value) => setLinea(Math.min(value, lineaPreaprobada))}
                  min={lineaMin}
                  max={lineaPreaprobada}
                  step={lineaStep}
                  minLabel={formatCurrencyWhole(lineaMin)}
                  maxLabel={`Preautorizado ${formatCurrencyWhole(lineaPreaprobada)}`}
                  loading={false}
                  testId="oferta-factoraje-linea-slider"
                />
                <Typography variant="body3" color="tokens.textTertiary" component="p" sx={{ mt: 0.75 }}>
                  Puedes tomar menos de tu línea preautorizada; no puedes subirla por encima de{" "}
                  {formatCurrencyWhole(lineaPreaprobada)}.
                </Typography>
              </Box>
            )}
          </CardPreOferta>

          <Paper variant="lendia_paper" sx={{ mt: 2 }}>
            <CardTitulo
              title="Límites por empresa"
              sub="Cada empresa tiene un monto máximo y un plazo máximo de factura autorizados según su riesgo. Puedes operar con cada una hasta su límite, siempre dentro de tu línea."
            />
            {/* `.fxu-rangos`: chip del color de la empresa (el mismo que en el reparto de la línea), nombre y límite. */}
            <Box
              sx={{
                mt: "12px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                gap: "8px 20px",
              }}
            >
              {LIMITES_POR_EMPRESA_DUMMY.map((empresa, i) => (
                <Box key={empresa.name} sx={{ display: "grid", gridTemplateColumns: "4px 1fr", gap: "10px" }}>
                  <Box sx={{ borderRadius: "3px", minHeight: 26, bgcolor: getSegmentColor(theme, i) }} />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "text.primary",
                        lineHeight: 1.3,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {empresa.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        color: "tokens.textTertiary",
                        fontVariantNumeric: "tabular-nums",
                        mt: "1px",
                      }}
                    >
                      Máximo {formatCurrencyWhole(empresa.maxAmount)} · plazo hasta {empresa.maxTerm} días
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper variant="lendia_paper" sx={{ mt: 2 }}>
            <CardTitulo
              title="Cómo se ve una operación"
              sub={`Ejemplo con una factura de ${formatCurrencyWhole(facturaEjemplo)} a ${EJEMPLO_DIAS} días.`}
            />
            <Box sx={{ mt: "14px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
              <Box>
                <Typography sx={STAT_LABEL_SX}>Anticipo ({aforo}%)</Typography>
                <Typography sx={STAT_VALUE_SX}>{formatCurrencyWhole(anticipo)}</Typography>
              </Box>
              <Box>
                <Typography sx={STAT_LABEL_SX}>Costo total</Typography>
                <Typography sx={STAT_VALUE_SX}>{formatCurrencyWhole(costoEjemplo)}</Typography>
              </Box>
              <Box>
                <Typography sx={STAT_LABEL_SX}>Recibes hoy</Typography>
                <Typography sx={[STAT_VALUE_SX, { color: "primary.accent" }]}>
                  {formatCurrencyWhole(anticipo - costoEjemplo)}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ m: "16px 0 12px", opacity: 0.6 }} />
            <Typography sx={{ fontSize: 13.5, color: "tokens.textTertiary", lineHeight: 1.55 }}>
              El {100 - aforo}% restante se te liquida cuando tu cliente paga la factura.
            </Typography>
          </Paper>

          <StepActions
            onNext={handleAccept}
            nextLabel="Aceptar y asignar mi línea"
            nextDisabled={isSubmitting}
            nextTestId="oferta-factoraje-aceptar-button"
          />
        </>
      )}
    </Box>
  );
};
